import { CommonModule } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { animate, style, transition, trigger } from '@angular/animations';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  LucideAngularModule,
  House,
  ClipboardList,
  Users,
  CalendarDays,
  Wallet,
  ChartColumn,
  ChevronDown,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  Laptop,
  Files,
  UserPlus,
  FileText,
  Settings,
} from 'lucide-angular';
import { AppService } from 'src/app/app.service';
import { AuthService } from 'src/app/authentication/auth.service';
import { PERMISSIONS } from 'src/app/core/constants/permissions';

// Maps a group menu id to the route prefixes considered "inside" that group.
const GROUP_ROUTES = {
  employeeMenu: ['/employee', '/pay/loan'],
  leaveMenu: ['/time'],
  payrollMenu: ['/pay/payroll', '/pay/payslips', '/pay/grade'],
  recruitmentMenu: ['/recruitment', '/careers'],
} as const;

type GroupId = keyof typeof GROUP_ROUTES;
type IconRef = typeof House;

type LeafItem = {
  kind: 'leaf';
  label: string;
  route: string;
  icon: IconRef;
};

type GroupChild = {
  label: string;
  route: string | string[];
};

type GroupItem = {
  kind: 'group';
  id: GroupId;
  label: string;
  icon: IconRef;
  children: GroupChild[];
};

type MenuItem = LeafItem | GroupItem;

type PopoverState = {
  id: string;
  label: string;
  children: GroupChild[];
  top: number;
};

const POPOVER_CLOSE_DELAY_MS = 120;

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [RouterLink, RouterLinkActive, CommonModule, LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideExpand', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('180ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms ease-in', style({ height: 0, opacity: 0 })),
      ]),
    ]),
    trigger('popoverFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-4px)' }),
        animate('140ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0, transform: 'translateX(-4px)' })),
      ]),
    ]),
  ],
})
export class SidenavComponent {
  readonly isExpanded = input(true, { transform: (v: boolean | undefined) => v ?? true });
  readonly toggleSidebar = output<void>();

  private readonly appService = inject(AppService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private readonly menuStates = signal<Record<string, boolean>>({});

  private readonly navEnd = toSignal(
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
  );
  private readonly currentUrl = computed(() => this.navEnd()?.urlAfterRedirects ?? this.router.url);

  private readonly overtimeTitle = computed(() => this.appService.overtimeTitle());
  private readonly overtimeRoute = computed(() => this.appService.overtimeRoute());
  private readonly curCompanyId = computed(() => this.appService.currentCompId);
  private readonly publicCareersRoute = computed(() => {
    // Public careers URLs are keyed by the short companyKey slug ("mediscope"),
    // never the display name — that produced /careers/jobs/Acme%2520Corp.
    const companyKey = this.appService.userInfo?.userCompanySettings?.companyDetails?.companyKey;
    return ['/careers', companyKey ?? 'zelustech'];
  });

  readonly chevronUp = ChevronUp;
  readonly chevronDown = ChevronDown;
  readonly collapseIcon = ChevronsLeft;
  readonly expandIcon = ChevronsRight;

  readonly iconSize = computed(() => (this.isExpanded() ? 18 : 20));
  readonly icons = {
    home: House,
    worklist: ClipboardList,
    recruitment: UserPlus,
    employee: Users,
    time: CalendarDays,
    payroll: Wallet,
    reports: ChartColumn,
    documents: FileText,
    settings: Settings,
    devices: Laptop,
    chevronDown: ChevronDown,
    chevronUp: ChevronUp,
    collapse: ChevronsLeft,
    expand: ChevronsRight,
  };

  // Unified popover state — used for both leaf labels and group flyouts in collapsed mode.
  private readonly popoverState = signal<PopoverState | null>(null);
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  // Element that opened the current popover; focus returns here on Escape.
  private triggerEl: HTMLElement | null = null;

  private readonly flyoutLinks = viewChildren<ElementRef<HTMLAnchorElement>>('flyoutLink');

  readonly popover = computed(() => (this.isExpanded() ? null : this.popoverState()));

  readonly menu = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [{ kind: 'leaf', label: 'Home', route: '/home', icon: House }];

    // Self-service, read-only view of the employee's own devices. The route is
    // intentionally ungated so anyone can reach it, but showing this alongside
    // the admin `Devices` leaf below is redundant (and double-highlights the
    // nav) — admins get their own assignments from their profile's Devices tab.
    if (!this.authService.can(PERMISSIONS.DEVICE.ASSET.READ)) {
      items.push({
        kind: 'leaf',
        label: 'My Devices',
        route: '/employee/my-devices',
        icon: Laptop,
      });
    }

    if (this.authService.can(PERMISSIONS.APPROVAL.REQUEST.VIEW)) {
      items.push({ kind: 'leaf', label: 'Worklist', route: '/worklist', icon: ClipboardList });
    }

    items.push({
      kind: 'group',
      id: 'employeeMenu',
      label: 'Employee',
      icon: Users,
      children: [
        // The Profile entry opens the company directory, which needs read_all;
        // plain `read` only reaches your own record.
        ...(this.authService.can(PERMISSIONS.EMPLOYEE.PROFILE.READ_ALL)
          ? [{ label: 'Profile', route: '/employee/profile' }]
          : []),
        // The Dependent entry opens the company-wide dependent list; plain
        // `read` only reaches your own, which the profile view already shows.
        ...(this.authService.can(PERMISSIONS.EMPLOYEE.DEPENDENT.READ_ALL)
          ? [{ label: 'Dependent', route: '/employee/dependent' }]
          : []),
        // These open the company-wide allowance/deduction lists; plain `read`
        // only reaches your own entries, which the profile view already shows.
        ...(this.authService.can(PERMISSIONS.EMPLOYEE.ALLOWANCE_DEDUCTION.READ_ALL)
          ? [
              { label: 'Allowances', route: '/employee/allowance' },
              { label: 'Deductions', route: '/employee/deduction' },
            ]
          : []),
        ...(this.curCompanyId() === 2 &&
        this.authService.can(PERMISSIONS.EMPLOYEE.ALLOWANCE_DEDUCTION.READ_ALL)
          ? [{ label: 'Loyalty Deduction', route: '/employee/loyalty-deduction' }]
          : []),
        // The /pay/loan area is the company-wide admin list; self-service employees
        // reach their own loans through the profile Loans tab instead.
        ...(this.authService.can(PERMISSIONS.PAY.LOAN.READ_ALL)
          ? [{ label: 'Loan', route: '/pay/loan' }]
          : []),
        ...(this.authService.can(PERMISSIONS.EMPLOYEE.ORG_CHART.READ)
          ? [{ label: 'Org Chart', route: '/employee/organization-chart' }]
          : []),
      ],
    });

    items.push({
      kind: 'group',
      id: 'leaveMenu',
      label: 'Time',
      icon: CalendarDays,
      children: [
        // The roster page, not a self-service view — match its route guard
        // ([READ_ALL, READ_TEAM]), or the self READ code every employee holds
        // puts a link to a 403 in everyone's nav.
        ...(this.authService.canAny(
          PERMISSIONS.TIME.LEAVE_BALANCE.READ_ALL,
          PERMISSIONS.TIME.LEAVE_BALANCE.READ_TEAM,
        )
          ? [{ label: 'Leave Balance', route: '/time/leave-balance' }]
          : []),
        ...(this.authService.can(PERMISSIONS.TIME.LEAVE.READ)
          ? [{ label: 'Leave Request', route: '/time/leave-request' }]
          : []),
        ...(this.authService.can(PERMISSIONS.TIME.OVERTIME.READ)
          ? [{ label: this.overtimeTitle(), route: this.overtimeRoute() }]
          : []),
        ...(this.authService.can(PERMISSIONS.TIME.LEAVE_GRANT.READ_ALL)
          ? [{ label: 'Leave Grant', route: '/time/leave-grant' }]
          : []),
        ...(this.authService.can(PERMISSIONS.TIME.LEAVE_ACCRUAL.READ)
          ? [{ label: 'Leave Processing', route: '/time/leave-processing' }]
          : []),
      ],
    });

    items.push({
      kind: 'group',
      id: 'payrollMenu',
      label: 'Payroll',
      icon: Wallet,
      children: [
        ...(this.authService.can(PERMISSIONS.PAY.PAYROLL.READ_ALL)
          ? [{ label: 'Payroll Processing', route: '/pay/payroll' }]
          : []),
        // READ_ALL, not READ: /pay/payslips is the company-wide list.
        ...(this.authService.can(PERMISSIONS.PAY.PAYROLL.READ_ALL)
          ? [{ label: 'Payslip', route: '/pay/payslips' }]
          : []),
        ...(this.authService.can(PERMISSIONS.PAY.GRADE.READ)
          ? [{ label: 'Grade', route: '/pay/grade' }]
          : []),
      ],
    });

    items.push({
      kind: 'group',
      id: 'recruitmentMenu',
      label: 'Recruitment',
      icon: UserPlus,
      children: [
        // { label: 'Dashboard', route: '/recruitment/dashboard' },
        ...(this.authService.can(PERMISSIONS.RECRUITMENT.JOB.READ)
          ? [{ label: 'Job Listings', route: '/recruitment/job-listings' }]
          : []),
        ...(this.authService.can(PERMISSIONS.RECRUITMENT.APPLICANT.READ)
          ? [{ label: 'Applicants', route: '/recruitment/applicants' }]
          : []),
        ...(this.authService.can(PERMISSIONS.RECRUITMENT.INTERVIEW.READ)
          ? [{ label: 'Interviews', route: '/recruitment/interviews' }]
          : []),
        ...(this.authService.can(PERMISSIONS.RECRUITMENT.OFFER.READ)
          ? [{ label: 'Job Offers', route: '/recruitment/offers' }]
          : []),
        ...(this.authService.can(PERMISSIONS.RECRUITMENT.SCORECARD.READ)
          ? [{ label: 'Scorecards', route: '/recruitment/scorecard' }]
          : []),
        { label: 'Public Careers', route: this.publicCareersRoute() },
      ],
    });

    if (this.authService.can(PERMISSIONS.REPORT.DASHBOARD.READ)) {
      items.push({ kind: 'leaf', label: 'Reports', route: '/reports/list', icon: ChartColumn });
    }
    // items.push({ kind: 'leaf', label: 'Documents', route: '/documents', icon: Files });
    if (this.authService.can(PERMISSIONS.DEVICE.ASSET.READ)) {
      items.push({ kind: 'leaf', label: 'Devices', route: '/devices', icon: Laptop });
    }

    // Every child of a group is permission-gated, so a group can end up with
    // nothing in it — drop those rather than render an expander onto nothing.
    return items.filter((item) => item.kind === 'leaf' || item.children.length > 0);
  });

  constructor() {
    // Keep menu open-state in sync with the active route and expansion state,
    // and close any open popover whenever the sidebar expands or the route changes.
    effect(() => {
      const url = this.currentUrl();
      const expanded = this.isExpanded();
      this.closePopover();
      const current = untracked(this.menuStates);
      if (!expanded) {
        if (Object.keys(current).length > 0) this.menuStates.set({});
        return;
      }
      const next: Record<string, boolean> = { ...current };
      let changed = false;
      for (const menuId of Object.keys(GROUP_ROUTES) as GroupId[]) {
        if (this.isGroupActiveFor(menuId, url) && !next[menuId]) {
          next[menuId] = true;
          changed = true;
        }
      }
      if (changed) this.menuStates.set(next);
    });
  }

  toggleMenu(menuId: GroupId, event?: MouseEvent): void {
    // Collapsed: a click pins/unpins the flyout for that group.
    if (!this.isExpanded()) {
      if (this.popoverState()?.id === menuId) {
        this.closePopover();
      } else {
        const item = this.menu().find((m): m is GroupItem => m.kind === 'group' && m.id === menuId);
        if (item) {
          this.openPopover(
            item.id,
            item.label,
            item.children,
            event?.currentTarget as HTMLElement | undefined,
          );
        }
      }
      return;
    }
    this.menuStates.update((s) => ({ ...s, [menuId]: !s[menuId] }));
  }

  isMenuOpen(menuId: GroupId): boolean {
    return !!this.menuStates()[menuId];
  }

  isGroupActive(menuId: GroupId): boolean {
    return this.isGroupActiveFor(menuId, this.currentUrl());
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  // ---- Popover hover handlers ----

  onLeafEnter(event: MouseEvent, id: string, label: string): void {
    if (this.isExpanded()) return;
    this.cancelClose();
    this.openPopover(id, label, [], event.currentTarget as HTMLElement);
  }

  onGroupEnter(event: MouseEvent, item: GroupItem): void {
    if (this.isExpanded()) return;
    this.cancelClose();
    this.openPopover(item.id, item.label, item.children, event.currentTarget as HTMLElement);
  }

  onRowLeave(): void {
    if (this.isExpanded()) return;
    this.scheduleClose();
  }

  onPopoverEnter(): void {
    this.cancelClose();
  }

  onPopoverLeave(): void {
    this.scheduleClose();
  }

  onPopoverItemClick(): void {
    this.closePopover();
  }

  onNavScroll(): void {
    // Fixed-position popover would drift if the menu scrolls; close it.
    this.closePopover();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // The popover's `top` is computed once from the trigger's bounding rect;
    // a resize can invalidate it (or the trigger itself), so just close it.
    this.closePopover();
  }

  // ---- Keyboard handlers ----

  onGroupKeydown(event: KeyboardEvent, item: GroupItem): void {
    if (this.isExpanded()) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      const btn = event.currentTarget as HTMLElement;
      this.triggerEl = btn;
      this.openPopover(item.id, item.label, item.children, btn);
      // Defer focus until the flyout has rendered.
      queueMicrotask(() => this.flyoutLinks()[0]?.nativeElement.focus());
    }
  }

  onPopoverKeydown(event: KeyboardEvent): void {
    const links = this.flyoutLinks().map((r) => r.nativeElement);
    if (links.length === 0) return;
    const idx = links.indexOf(document.activeElement as HTMLAnchorElement);

    switch (event.key) {
      case 'Escape':
      case 'ArrowLeft':
        event.preventDefault();
        this.closePopoverAndRestoreFocus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        links[(idx + 1) % links.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        links[(idx <= 0 ? links.length : idx) - 1]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        links[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        links[links.length - 1]?.focus();
        break;
    }
  }

  private closePopoverAndRestoreFocus(): void {
    const trigger = this.triggerEl;
    this.closePopover();
    trigger?.focus();
  }

  private openPopover(
    id: string,
    label: string,
    children: GroupChild[],
    anchor: HTMLElement | undefined,
  ): void {
    const rawTop = anchor ? anchor.getBoundingClientRect().top : (this.popoverState()?.top ?? 0);
    // Clamp so a flyout opened near the bottom of a short viewport stays
    // on-screen instead of running off the bottom edge.
    const estimatedHeight = 40 + children.length * 32; // header + one row per child
    const top = Math.max(0, Math.min(rawTop, window.innerHeight - estimatedHeight - 8));
    this.popoverState.set({ id, label, children, top });
  }

  private closePopover(): void {
    this.cancelClose();
    this.popoverState.set(null);
    this.triggerEl = null;
  }

  private scheduleClose(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      this.popoverState.set(null);
      this.triggerEl = null;
    }, POPOVER_CLOSE_DELAY_MS);
  }

  private cancelClose(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private isGroupActiveFor(menuId: GroupId, url: string): boolean {
    return GROUP_ROUTES[menuId].some((r) => url.startsWith(r));
  }
}
